'use client'
import Bugsnag from '@bugsnag/js'
import { Button, Input, LuneTheme, useDevice } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Text from '@mui/material/Typography'
import { useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

import { useNewsletter } from '@/hooks/useNewsLetter'
import { HUBSPOT_FORM_IDS } from '@/utills/constants'

export const SubscribeToNewsletter = ({
    noBorder,
    noMargin,
}: {
    noBorder?: boolean
    noMargin?: boolean
}) => {
    const theme = LuneTheme
    const { xsScreen } = useDevice()
    const hubspotFormId = HUBSPOT_FORM_IDS.blog
    const { isValidEmail, toHubspot } = useNewsletter(hubspotFormId)

    const [name, setName] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [nameError, setNameError] = useState<boolean>(false)
    const [emailError, setEmailError] = useState<boolean>(false)
    const [success, setSuccess] = useState<boolean>(false)
    const [debouncedEmail] = useDebounce(email, 1000)

    useEffect(() => {
        if (isValidEmail(email) || email === '') {
            setEmailError(false)
        } else {
            setEmailError(true)
        }
    }, [debouncedEmail, email, isValidEmail])

    useEffect(() => {
        setNameError(false)
    }, [name])

    const onSubmit = async () => {
        const validName = name.length
        const validEmail = isValidEmail(email)
        if (!validName || !validEmail) {
            !validName && setNameError(true)
            !validEmail && setEmailError(true)
            return
        }

        const data = await toHubspot({
            name,
            email,
            legalConsent: 'marketing',
        })
        if (data.status === 'error') {
            setSuccess(false)
            Bugsnag.notify(new Error(`Newsletter subscription error: ${hubspotFormId}`))
        } else {
            gtag('event', 'newsletter_signup', {})
            setSuccess(true)
            setName('')
            setEmail('')
        }
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <Stack
                direction="column"
                alignItems="center"
                spacing={xsScreen ? 3 : 4}
                sx={{
                    width: '100%',
                    maxWidth: '720px',
                    py: xsScreen ? 6 : 9,
                    borderTop: noBorder ? 'none' : `solid 1px ${theme.palette.Grey300}`,
                    borderBottom: noBorder ? 'none' : `solid 1px ${theme.palette.Grey300}`,
                    my: noMargin ? 0 : xsScreen ? 5 : 15,
                }}
            >
                {success ? (
                    <Text variant={'h6'}>
                        Thank you for subscribing to our newsletter, The Carbon Cache. Check your
                        inbox for a surprise from us!
                    </Text>
                ) : (
                    <>
                        <Text variant={'h6'} textAlign={'center'}>
                            Subscribe for the latest insights into driving climate positivity
                        </Text>
                        <Stack
                            direction={xsScreen ? 'column' : 'row'}
                            sx={{ width: '100%' }}
                            gap={1}
                        >
                            <Input
                                sx={{
                                    backgroundColor: nameError
                                        ? theme.palette.Red50
                                        : theme.palette.Grey100,
                                }}
                                name="name"
                                type="text"
                                placeholder="Name"
                                wide
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                            <Input
                                sx={{
                                    backgroundColor: emailError
                                        ? theme.palette.Red50
                                        : theme.palette.Grey100,
                                }}
                                name="email"
                                type="text"
                                placeholder="Email"
                                wide
                                value={email}
                                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                            />
                        </Stack>
                        <Button
                            type="submit"
                            variant="outlined"
                            wide
                            sx={{ textTransform: 'capitalize' }}
                            onClick={onSubmit}
                        >
                            Subscribe
                        </Button>
                    </>
                )}
            </Stack>
        </Box>
    )
}
